import { lazy, type ComponentType, type LazyExoticComponent } from "react";

import { logger } from "@/lib/logging/logger";

const log = logger.child("lazyWithRetry");

// One reload per session, max. A failed dynamic import is usually one of two things: a transient
// network/CDN hiccup (fixed by retrying the same import) or a stale chunk after a fresh deploy — the
// hashed filename in this tab's index.html no longer exists on the origin (fixed only by reloading to
// pull a new index.html + manifest). We try retries first, then fall back to a single guarded reload.
// The guard prevents an infinite reload loop when the chunk is genuinely gone (e.g. the origin is
// down, or — as e2e against a rate-limited WAF can see — every request is being blocked).
const RELOAD_GUARD_KEY = "lazyWithRetry:reloaded";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const hasReloadedThisSession = (): boolean => {
  try {
    return typeof window !== "undefined" && window.sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    // sessionStorage can throw (privacy mode, disabled storage). Treat as "already reloaded" so we
    // never loop — fall through to the error boundary instead.
    return true;
  }
};

const markReloaded = (reloaded: boolean) => {
  try {
    if (typeof window === "undefined") return;
    if (reloaded) {
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    } else {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    }
  } catch {
    // Ignore storage failures — the worst case is we don't reload, which is the safe direction.
  }
};

async function importWithRetry<T>(factory: () => Promise<T>, retries = 2, delayMs = 400): Promise<T> {
  try {
    return await factory();
  } catch (error) {
    if (retries > 0) {
      log.warn("Dynamic import failed; retrying", { attributes: { retriesLeft: retries } });
      await wait(delayMs);
      return importWithRetry(factory, retries - 1, delayMs * 2);
    }
    throw error;
  }
}

/**
 * Drop-in replacement for `React.lazy` that survives a flaky chunk fetch: it retries the dynamic
 * import a couple of times, and if that still fails, forces one full page reload (guarded so it can
 * only happen once per session) to recover from a stale post-deploy chunk. If the reload has already
 * been spent, the error propagates to the app error boundary as before.
 */
// `any` mirrors React's own `lazy` signature — it lets each call site keep its precise component
// (and prop) type via inference. `unknown` here would reject components that require props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await importWithRetry(factory);
      // Recovered (or loaded first try) — release the guard so a later, unrelated chunk failure can
      // still earn its own reload.
      markReloaded(false);
      return mod;
    } catch (error) {
      if (!hasReloadedThisSession()) {
        log.warn("Dynamic import still failing after retries; reloading once to refresh chunks", {
          error: error instanceof Error ? error : undefined,
        });
        markReloaded(true);
        window.location.reload();
        // Keep React on the Suspense fallback while the reload navigates away.
        return new Promise<{ default: T }>(() => {});
      }
      log.error("Dynamic import failed after retries and reload; surfacing to error boundary", {
        error: error instanceof Error ? error : undefined,
      });
      throw error;
    }
  });
}
